import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  CommercialContact,
  CommercialContactConnection,
} from 'src/app/model/commercial-services/commercial-contact';
import { CommercialContactUpdateDto } from 'src/app/model/commercial-services/commercial-contact-update-dto';
import { isNullOrUndefined } from 'src/app/utils/utils';
import {
  CommercialContactMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';

@Injectable({
  providedIn: 'root',
})
export class CommercialContactBuilderService {
  buildCommercialContactValidationMessages(): ValidationConfig<CommercialContactMessageKeys> {
    return {
      messages: {
        firstName: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.FIRST_NAME_REQUIRED',
        },
        lastName: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.LAST_NAME_REQUIRED',
        },
        companyId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.COMPANY_REQUIRED',
        },
        jobRoleId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.ROLE_REQUIRED',
        },
        emailAddress: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.EMAIL_REQUIRED',
          email: 'COMMERCIAL_SERVICES.VALIDATION.EMAIL_INVALID',
        },
      },
      params: {},
    };
  }

  buildNewCommercialContact(): CommercialContact {
    return {
      id: '',
      firstName: '',
      lastName: '',
      fullName: '',
      companyId: null,
      jobRoleId: null,
      deskPhone: null,
      mobilePhone: null,
      emailAddress: '',
      ice: null,
      company: null,
      jobRole: null,
      createdAt: '',
      createdBy: '',
      updatedAt: '',
      updater: '',
      version: 0,
    } as unknown as CommercialContact;
  }

  buildCommercialContact(i: any): CommercialContact | null {
    if (isNullOrUndefined(i)) {
      return null;
    }

    return {
      id: i.id,
      firstName: i.firstName,
      lastName: i.lastName,
      fullName: `${i.firstName ?? ''} ${i.lastName ?? ''}`.trim(),
      companyId: i.companyId,
      jobRoleId: i.jobRoleId,
      deskPhone: i.deskPhone,
      mobilePhone: i.mobilePhone,
      emailAddress: i.emailAddress,
      ice: i.ice,
      company: i.Company,
      jobRole: i.JobRole,
      createdAt: i.createdAt,
      createdBy: i.createdBy,
      updatedAt: i.updatedAt,
      updater: i.updater,
      deletedAt: i.deletedAt,
      version: i.version,
    };
  }

  buildCommercialContactConnection(item: any): CommercialContactConnection {
    return {
      items: item.items.map((c: any) => this.buildCommercialContact(c)),
      totalRecords: item.totalRecords,
    };
  }

  buildCommercialContactQuery(filters: any) {
    const query: Record<string, string | null> = {};

    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      const value = Array.isArray(filter) ? filter[0]?.value : filter?.value;
      if (value !== null && value !== undefined && value !== '') {
        query[key] = typeof value === 'string' ? value.trim() : value;
      }
    });

    return query;
  }

  buildForm(fb: UntypedFormBuilder, contact: any): UntypedFormGroup {
    return fb.group({
      id: [contact.id],
      firstName: [contact.firstName, Validators.required],
      lastName: [contact.lastName, Validators.required],
      companyId: [contact.companyId, Validators.required],
      jobRoleId: [contact.jobRoleId, Validators.required],
      deskPhone: [contact.deskPhone],
      mobilePhone: [contact.mobilePhone],
      emailAddress: [
        contact.emailAddress,
        [Validators.required, Validators.email],
      ],
      ice: [contact.ice],
      version: [contact.version],
    });
  }

  updateForm(form: UntypedFormGroup, contact: any, writeAccess: boolean) {
    form.patchValue({
      id: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      companyId: contact.companyId,
      jobRoleId: contact.jobRoleId,
      deskPhone: contact.deskPhone,
      mobilePhone: contact.mobilePhone,
      emailAddress: contact.emailAddress,
      ice: contact.ice,
      version: contact.version,
    });
    form.markAsPristine();
    form.markAsUntouched();

    if (writeAccess === false) {
      form.disable();
    }
  }

  buildCreateObject(contact: any): CommercialContactUpdateDto {
    return {
      firstName: contact.firstName,
      lastName: contact.lastName,
      companyId: contact.companyId,
      jobRoleId: contact.jobRoleId,
      deskPhone: contact.deskPhone || null,
      mobilePhone: contact.mobilePhone || null,
      emailAddress: contact.emailAddress,
      ice: contact.ice ? contact.ice : null,
    };
  }

  buildUpdateStatement(
    contact: CommercialContact,
    changes: any
  ): CommercialContactUpdateDto {
    const retVal: CommercialContactUpdateDto = {
      expectedVersion: contact.version + 1,
    };

    Object.keys(changes).forEach((key) => {
      if (changes[key] !== undefined) {
        (retVal as any)[key] = changes[key];
      }
    });

    return retVal;
  }
}
